import { Controller } from "@hotwired/stimulus"
import { retoneStatusBadge } from "../lib/status_badge"

// Signature settings: you can't enable a signature that doesn't exist. Keeps the
// "Enable signature on all outgoing messages" toggle disabled — and forced off —
// whenever the TipTap editor has no text content, and re-enables it as soon as
// the editor has content. The editor stores its HTML in a hidden input and fires
// a bubbling `input` event on every change, so a form-level `input` action drives
// `refresh`. This is the client half of the rule; User#signature_content_required_when_enabled
// enforces the same invariant server-side so a raw PATCH can't bypass it.
// An optional `status` target (Settings → Signature's header pill, #18893)
// reads On / Off with the toggle, so it never claims a state the box has left.
export const STATUS_TONES = { on: "success", off: "secondary" }

export default class extends Controller {
  static targets = ["toggle", "status"]

  connect() {
    // The signature hidden input is owned by the shared composer island;
    // locate it by name rather than adding a second Stimulus target to the view.
    this.contentInput = this.element.querySelector("input[name='user[signature]']")
    this.toggleTarget.disabled = this.editorIsEmpty()
    // The legacy correction below waits one frame: card-dirty (on the form,
    // connected first) snapshots on ITS next frame, and frame callbacks run in
    // the order they were queued, so the correction lands AFTER the baseline
    // and reads dirty — Save can commit it. Run on connect, it became the
    // baseline and could never be saved (review of the BUTTON-9 audit).
    this._frame = requestAnimationFrame(() => this.refresh())
  }

  disconnect() {
    cancelAnimationFrame(this._frame)
  }

  refresh() {
    const empty = this.editorIsEmpty()
    this.toggleTarget.disabled = empty
    // Force the toggle off when there's nothing to enable. If it was actually on
    // (e.g. a legacy signature_enabled=true + blank-signature row loading here),
    // dispatch a change event so sibling controllers — notably card-dirty, which
    // snapshots on connect and only re-syncs on input/change — recognize the form
    // as dirty and let the user save the correction. A bare `checked = false`
    // assignment fires no event, which would otherwise strand card-dirty on its
    // stale checked=true snapshot (Save disabled, Cancel hidden).
    if (empty && this.toggleTarget.checked) {
      this.toggleTarget.checked = false
      this.toggleTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }
    this.syncStatus()
  }

  syncStatus() {
    const on = this.toggleTarget.checked
    this.statusTargets.forEach((pill) => {
      pill.textContent = on ? "On" : "Off"
      retoneStatusBadge(pill, on ? STATUS_TONES.on : STATUS_TONES.off)
    })
  }

  editorIsEmpty() {
    if (!this.contentInput) return true

    const probe = document.createElement("div")
    probe.innerHTML = this.contentInput.value || ""
    return probe.textContent.trim().length === 0
  }
}
